// Общий switch по типу блока — раньше был продублирован дословно в
// MobilePreviewFrame и BlockThumbnail, различаясь только тем, что мобильное
// превью прокидывает onChange в DesignBlockPreview (тумбнейл — нет, он read-only).
import { Fragment } from 'react'
import type { Block, DesignElement } from '@/types/blocks'
import type { Instruction } from '@/features/instructions/types'
import type { ContentLang } from '@/features/editor/editorSlice'
import type { PreviewRun } from '../../preview/previewRun'
import { InfoBlockPreview } from './InfoBlockPreview'
import { QuizBlockPreview } from './QuizBlockPreview'
import { MatchBlockPreview } from './MatchBlockPreview'
import { DisabledBlockPreview } from './DisabledBlockPreview'
import { VideoBlockPreview } from './VideoBlockPreview'
import { ArticleBlockPreview } from './ArticleBlockPreview'
import { DesignBlockPreview } from './DesignBlockPreview'

export interface RenderBlockPreviewOpts {
  /** Передаётся только когда правка дизайн-блока разрешена (WYSIWYG в большом превью). */
  onDesignChange?: (elements: DesignElement[]) => void
  /**
   * Выбор статьи в пустом блоке статьи на холсте (PRD #71, ф.3). Передаётся только
   * когда холст правится и блок уже есть на сервере; без него список погашен.
   */
  onArticlePick?: (articleId: number, article: Instruction) => void
  /**
   * Прохождение урока в полноэкранном предпросмотре. Без него превью работают
   * ровно как в редакторе — статично.
   */
  run?: PreviewRun
}

/**
 * Ключ ПОСЕЩЕНИЯ экрана стоит здесь, а не у вызывающих: превью держат
 * собственное состояние взаимодействия (отметки сопоставления), два экрана
 * одного типа подряд реконсилируются в ОДИН экземпляр, и отметка прошлого
 * экрана переезжала на следующий. Место одно на всех потребителей прохождения
 * — забыть ключ в новом вызове нельзя. Вне прохождения ключа нет: у ленты
 * миниатюр и редактора своя реконсиляция.
 */
export const renderBlockPreview = (
  block: Block,
  contentLang: ContentLang,
  opts?: RenderBlockPreviewOpts,
) => {
  const run = opts?.run
  const preview = renderPreview(block, contentLang, opts)
  return run ? <Fragment key={run.visitKey}>{preview}</Fragment> : preview
}

const renderPreview = (block: Block, contentLang: ContentLang, opts?: RenderBlockPreviewOpts) => {
  const run = opts?.run
  switch (block.type) {
    case 'info':
      return <InfoBlockPreview block={block} contentLang={contentLang} run={run} />
    case 'quiz':
      return <QuizBlockPreview block={block} contentLang={contentLang} run={run} />
    case 'match':
      return <MatchBlockPreview block={block} contentLang={contentLang} run={run} />
    case 'video':
      return <VideoBlockPreview block={block} contentLang={contentLang} run={run} />
    case 'article':
      return (
        <ArticleBlockPreview
          block={block}
          contentLang={contentLang}
          run={run}
          onArticlePick={run ? undefined : opts?.onArticlePick}
        />
      )
    case 'design':
      return (
        <DesignBlockPreview
          block={block}
          contentLang={contentLang}
          // В прохождении правка выключена структурно, а не договорённостью:
          // клик по элементу иначе одновременно «отвечает» и редактирует.
          onChange={run ? undefined : opts?.onDesignChange}
          run={run}
        />
      )
    default:
      return <DisabledBlockPreview onNext={run?.onCta} />
  }
}
